import { ArrowUpOutlined, EnvironmentOutlined } from "@ant-design/icons"
import { Progress, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { empty } from "../../../constants/placeholders"
import { formatDocumentDate } from "../../Files/helpers/document-date.ts"
import { budgetFormat } from "../../../utils/number/budget-format.ts"
import { getProgressColor } from "../../Projects/constants/project-progress.ts"
import { globalMapStatusStyle } from "../helpers/status-style"
import type { EntityQuickView } from "../redux/global-map.types"

const { Text } = Typography

interface QuickViewOverviewProps {
  record: EntityQuickView
  // Formats a real instant (`created`) in the account's own zone. The
  // lifecycle's other dates are bare calendar days and are read through
  // `formatDocumentDate` instead — see `calendarDay` below.
  formatDate: (value: string | null | undefined) => string
}

// Start/End/Appointment are calendar days on the wire (`YYYY-MM-DD`), not
// instants: re-zoning one turns its midnight into the evening before for any
// browser west of UTC, which is exactly how "Jul 2" rendered as "July 1".
// `formatDocumentDate` is the reader this repo already uses for that.
const calendarDay = (value: string | null | undefined): string =>
  formatDocumentDate(value) ?? empty

function PersonRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="truncate text-[11px] font-medium uppercase tracking-wide text-slate-400">
        {label}
      </span>
      <span className="truncate text-[13px] font-medium text-slate-800">
        {value}
      </span>
    </div>
  )
}

function SummaryCell({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="truncate text-[11px] font-medium uppercase tracking-wide text-slate-400">
        {label}
      </span>
      <span className="truncate text-[13px] font-medium text-slate-800">
        {children}
      </span>
    </div>
  )
}

/**
 * The panel's Overview tab for both entity types.
 *
 * Everything here is a field the record's own payload carries — people, the
 * lifecycle dates, the address, and (for a project the caller may see money
 * for) the canonical contract/expenses/margin figures. A value the record
 * does not have renders as "—" rather than hiding its row, so the panel's
 * shape is the same for every record and an empty field reads as empty
 * rather than as missing UI.
 */
export function QuickViewOverview({
  record,
  formatDate
}: QuickViewOverviewProps) {
  const { t } = useTranslation()
  const isProject = record.entity_type === "project"
  const people = record.people
  const financials = isProject ? record.financials : null

  const appointment = record.lifecycle.appointment_date
    ? [
        calendarDay(record.lifecycle.appointment_date),
        record.lifecycle.appointment_time
      ]
        .filter(Boolean)
        .join(" · ")
    : empty

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3">
        <PersonRow
          label={t("globalMap.quickView.fields.owner")}
          value={people.owner ?? empty}
        />
        <PersonRow
          label={t(
            isProject
              ? "globalMap.quickView.fields.projectManager"
              : "globalMap.quickView.fields.salesperson"
          )}
          value={
            (isProject ? people.project_manager : people.salesperson) ?? empty
          }
        />
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-3 border-t border-gray-100 pt-3">
        <SummaryCell
          label={t(
            isProject
              ? "globalMap.quickView.fields.status"
              : "globalMap.quickView.fields.leadStage"
          )}
        >
          <span className="inline-flex items-center gap-1.5">
            <span
              aria-hidden
              className="size-2 shrink-0 rounded-full"
              style={{
                backgroundColor: globalMapStatusStyle(
                  record.entity_type,
                  record.status_label
                ).color
              }}
            />
            {record.status_label || empty}
          </span>
        </SummaryCell>
        <SummaryCell label={t("globalMap.quickView.fields.projectType")}>
          {record.project_type || empty}
        </SummaryCell>
        {isProject ? (
          <>
            <SummaryCell label={t("globalMap.quickView.fields.startDate")}>
              {calendarDay(record.lifecycle.start_date)}
            </SummaryCell>
            <SummaryCell label={t("globalMap.quickView.fields.endDate")}>
              {calendarDay(record.lifecycle.end_date)}
            </SummaryCell>
          </>
        ) : (
          <>
            <SummaryCell label={t("globalMap.quickView.fields.appointment")}>
              {appointment}
            </SummaryCell>
            <SummaryCell label={t("globalMap.quickView.fields.created")}>
              {formatDate(record.lifecycle.created) || empty}
            </SummaryCell>
          </>
        )}
      </div>

      {!isProject && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-3">
          <SummaryCell label={t("globalMap.quickView.fields.phone")}>
            {people.customer_phone || empty}
          </SummaryCell>
          <SummaryCell label={t("globalMap.quickView.fields.email")}>
            {people.customer_email || empty}
          </SummaryCell>
        </div>
      )}

      {isProject && (
        <div className="flex items-center gap-3">
          <span className="shrink-0 text-[11px] font-medium uppercase tracking-wide text-slate-400">
            {t("globalMap.quickView.fields.progress")}
          </span>
          <Progress
            percent={record.progress_percent ?? 0}
            size="small"
            strokeColor={getProgressColor(record.progress_percent ?? 0)}
            format={(percent) => `${percent}%`}
            className="!mb-0 min-w-0 flex-1"
          />
        </div>
      )}

      <div className="flex items-start gap-2 rounded-lg border border-gray-100 bg-slate-50/60 px-3 py-2.5">
        <EnvironmentOutlined
          aria-hidden
          className="mt-0.5 shrink-0 !text-[13px] text-slate-400"
        />
        <Text className="text-[13px] leading-snug text-slate-700">
          {record.location.address || empty}
        </Text>
      </div>

      {financials && (
        <div
          className="grid grid-cols-3 gap-2 border-t border-gray-100 pt-3"
          data-testid="global-map-quickview-financials"
        >
          <div className="flex min-w-0 flex-col gap-1">
            <span className="truncate text-[11px] font-medium uppercase tracking-wide text-slate-400">
              {t("globalMap.quickView.financial.contractValue")}
            </span>
            <span className="truncate text-[15px] font-semibold tabular-nums text-slate-900">
              {budgetFormat(financials.contract_value)}
            </span>
          </div>
          <div className="flex min-w-0 flex-col gap-1">
            <span className="truncate text-[11px] font-medium uppercase tracking-wide text-slate-400">
              {t("globalMap.quickView.financial.expenses")}
            </span>
            <span className="truncate text-[15px] font-semibold tabular-nums text-slate-900">
              {budgetFormat(financials.expenses)}
            </span>
          </div>
          <div className="flex min-w-0 flex-col gap-1">
            <span className="truncate text-[11px] font-medium uppercase tracking-wide text-slate-400">
              {t("globalMap.quickView.financial.margin")}
            </span>
            {/* Green with an up arrow only while the margin is actually
                positive — a loss-making project must not read as on track. */}
            <span
              className={`flex items-center gap-1 truncate text-[15px] font-semibold tabular-nums ${
                (financials.margin_percent ?? 0) >= 0
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              {financials.margin_percent === null
                ? empty
                : `${financials.margin_percent}%`}
              {(financials.margin_percent ?? 0) > 0 && (
                <ArrowUpOutlined aria-hidden className="!text-[11px]" />
              )}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default QuickViewOverview
